import { motion } from 'framer-motion';

interface StatItemProps {
  value: string;
  label: string;
}

interface FeatureItemProps {
  text: string;
}

/**
 * Right panel with animated branding for registration.
 */
export function RegisterRightPanel() {
  return (
    <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-brand-green via-brand-teal to-brand-blue relative overflow-hidden">
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-32 right-20 w-80 h-80 rounded-full bg-white/10 blur-3xl"
          animate={{ x: [0, -40, 0], y: [0, 40, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-32 left-20 w-72 h-72 rounded-full bg-white/10 blur-3xl"
          animate={{ x: [0, 30, 0], y: [0, -30, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />

      <div className="relative z-10 flex flex-col items-center justify-center w-full p-12 text-white">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center"
        >
          <motion.div
            className="mb-8"
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <img
              src="/vrmeet_logo.png"
              alt="VR Meet"
              className="h-28 w-28 mx-auto drop-shadow-2xl"
            />
          </motion.div>

          <h2 className="text-4xl font-bold mb-4">
            Join the Future of Meetings
          </h2>
          <p className="text-xl text-white/80 max-w-md mx-auto leading-relaxed">
            Create persistent team rooms,
            and collaborate in virtual reality.
          </p>
        </motion.div>

        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 flex gap-12 text-center"
        >
          <StatItem value="100%" label="Free to start" />
          <StatItem value="VR" label="Headset ready" />
          <StatItem value="24/7" label="Available" />
        </motion.div>

        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <p className="font-medium mb-4">What you'll get:</p>
          <ul className="space-y-3">
            <FeatureItem text="Persistent team rooms" />
            <FeatureItem text="VR headset support" />
            <FeatureItem text="Screen sharing" />
          </ul>
        </motion.div>
      </div>
    </div>
  );
}

function StatItem({ value, label }: StatItemProps) {
  return (
    <div>
      <p className="text-3xl font-bold">{value}</p>
      <p className="text-sm text-white/60">{label}</p>
    </div>
  );
}

function FeatureItem({ text }: FeatureItemProps) {
  return (
    <li className="flex items-center gap-3">
      <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      <span className="text-white/80">{text}</span>
    </li>
  );
}
